(() => {
  "use strict";
  const LEGACY_PROGRESSIER_HOST = "progressier.app";
  const LEGACY_PROGRESSIER_WORKER_PATH = "/progressier.js";
  const blockLegacyProgressierLoader = () => {
    const originalInsertBefore = Node.prototype.insertBefore;
    let blocking = true;
    const guardedInsertBefore = function (newNode, referenceNode) {
      if (blocking && newNode instanceof HTMLScriptElement) {
        try {
          const source = new URL(newNode.src || newNode.getAttribute("src") || "", window.location.href);
          if (source.hostname === LEGACY_PROGRESSIER_HOST) {
            newNode.dataset.brpwaBlocked = "progressier";
            return newNode;
          }
        } catch (_error) {}
      }
      return originalInsertBefore.call(this, newNode, referenceNode);
    };
    Node.prototype.insertBefore = guardedInsertBefore;
    const restore = () => {
      blocking = false;
      if (Node.prototype.insertBefore === guardedInsertBefore) Node.prototype.insertBefore = originalInsertBefore;
    };
    window.addEventListener("load", () => window.setTimeout(restore, 0), {once: true});
    window.setTimeout(restore, 15000);
  };
  blockLegacyProgressierLoader();

  const config = window.BRPWA || {};
  if (config.enabled !== true || !("serviceWorker" in navigator)) return;
  const WORKER_VERSION = "br-pwa-v6-candidate";
  const PROMPT_ID = "br-pwa-action-prompt";
  const APP_HOME_URL = "/?view=pwa";
  const LEGACY_INSTALL_URL = "https://install.page/broadwayred";
  const INSTALL_HASH = "#br-pwa-install";
  const standalone = window.matchMedia("(display-mode: standalone)").matches || window.navigator.standalone === true;
  const onAppHome = window.location.pathname === "/" && new URLSearchParams(window.location.search).get("view") === "pwa";
  if (standalone && window.location.pathname === "/" && !onAppHome) {
    window.location.replace(APP_HOME_URL);
    return;
  }
  let installPrompt = null;
  const state = (name, detail = {}) => window.dispatchEvent(new CustomEvent("brpwa:state", {
    detail: {name, ...detail}
  }));
  const vapidKey = value => {
    const padding = "=".repeat((4 - value.length % 4) % 4);
    const raw = atob((value + padding).replace(/-/g, "+").replace(/_/g, "/"));
    return Uint8Array.from(raw, character => character.charCodeAt(0));
  };
  const activeRegistration = async () => {
    const registration = await window.BRPWA.registrationPromise;
    if (registration.active) return registration;
    const worker = registration.installing || registration.waiting;
    if (!worker) return registration;
    await new Promise(resolve => worker.addEventListener("statechange", () => {
      if (worker.state === "activated") resolve();
    }));
    return registration;
  };
  const removeLegacyProgressierRuntime = async () => {
    const registrations = await navigator.serviceWorker.getRegistrations();
    await Promise.all(registrations.map(async registration => {
      const worker = registration.active || registration.waiting || registration.installing;
      if (!worker) return;
      try {
        if (new URL(worker.scriptURL).pathname === LEGACY_PROGRESSIER_WORKER_PATH) await registration.unregister();
      } catch (_error) {}
    }));
    if (!("caches" in window)) return;
    const cacheNames = await caches.keys();
    await Promise.all(cacheNames
      .filter(name => /^progressier(?:-|_|$)/i.test(name))
      .map(name => caches.delete(name)));
  };
  const removePrompt = () => document.getElementById(PROMPT_ID)?.remove();
  const whenBodyReady = callback => {
    if (document.body) callback();
    else document.addEventListener("DOMContentLoaded", callback, {once: true});
  };
  const dismissed = kind => {
    try { return window.sessionStorage.getItem(`brpwa_prompt_${kind}`) === "dismissed"; }
    catch (_error) { return false; }
  };
  const dismiss = kind => {
    try { window.sessionStorage.setItem(`brpwa_prompt_${kind}`, "dismissed"); }
    catch (_error) {}
    removePrompt();
  };
  const showActionPrompt = ({kind, title, body, actionLabel, action}) => {
    if (dismissed(kind)) return;
    whenBodyReady(() => {
      if (document.getElementById(PROMPT_ID)) return;
      const prompt = document.createElement("aside");
      prompt.id = PROMPT_ID;
      prompt.setAttribute("role", "dialog");
      prompt.setAttribute("aria-label", title);
      Object.assign(prompt.style, {
        position: "fixed", left: "16px", right: "16px", bottom: "16px",
        zIndex: "2147483000", maxWidth: "440px", margin: "0 auto", padding: "18px",
        borderRadius: "18px", background: "#111", color: "#fff",
        boxShadow: "0 12px 36px rgba(0,0,0,.32)", font: "16px/1.4 Arial,sans-serif"
      });
      const heading = document.createElement("strong");
      heading.textContent = title;
      heading.style.display = "block";
      heading.style.paddingRight = "36px";
      heading.style.fontSize = "18px";
      const copy = document.createElement("p");
      copy.textContent = body;
      copy.style.margin = "8px 0 14px";
      const actionButton = document.createElement("button");
      actionButton.type = "button";
      actionButton.textContent = actionLabel;
      Object.assign(actionButton.style, {
        width: "100%", padding: "12px 16px", border: "0", borderRadius: "999px",
        background: "#d9232e", color: "#fff", font: "700 16px Arial,sans-serif", cursor: "pointer"
      });
      const closeButton = document.createElement("button");
      closeButton.type = "button";
      closeButton.textContent = "×";
      closeButton.setAttribute("aria-label", "Chiudi");
      Object.assign(closeButton.style, {
        position: "absolute", top: "8px", right: "12px", border: "0",
        background: "transparent", color: "#fff", fontSize: "30px", cursor: "pointer"
      });
      closeButton.addEventListener("click", () => dismiss(kind));
      actionButton.addEventListener("click", async () => {
        actionButton.disabled = true;
        const originalLabel = actionButton.textContent;
        actionButton.textContent = "Attendi…";
        try {
          const completed = await action();
          if (completed) removePrompt();
          else {
            actionButton.disabled = false;
            actionButton.textContent = originalLabel;
          }
        } catch (_error) {
          actionButton.disabled = false;
          actionButton.textContent = "Riprova";
        }
      });
      prompt.append(heading, closeButton, copy, actionButton);
      document.body.append(prompt);
    });
  };

  navigator.serviceWorker.addEventListener("message", event => {
    const message = event.data || {};
    if (message.type === "BRPWA_VERSION") {
      state("worker_version", {version: String(message.version || "unknown")});
      return;
    }
    if (message.type !== "BRPWA_NOTIFICATION_CLICK") return;
    const target = new URL(String(message.deep_link || "/"), window.location.origin);
    if (target.origin !== window.location.origin) return;
    state("notification_click", {status: "navigating"});
    window.location.assign(target.href);
  });

  window.addEventListener("beforeinstallprompt", event => {
    event.preventDefault();
    installPrompt = event;
    state("install_available");
    showActionPrompt({
      kind: "install",
      title: "Installa Broadway Red",
      body: "Aggiungi Broadway Red al tuo dispositivo per aprire Shop, Club e Account come app.",
      actionLabel: "Installa app",
      action: async () => (await window.BRPWAInstall()).status === "accepted"
    });
  });
  window.addEventListener("appinstalled", () => {
    installPrompt = null;
    removePrompt();
    state("installed");
  });

  window.BRPWAInstall = async () => {
    if (!installPrompt) return {status: "manual_or_unavailable"};
    await installPrompt.prompt();
    const choice = await installPrompt.userChoice;
    if (choice.outcome === "accepted") installPrompt = null;
    state("install_choice", {status: choice.outcome});
    return {status: choice.outcome};
  };

  const showManualInstall = () => {
    const ios = /iPad|iPhone|iPod/.test(navigator.userAgent) ||
      (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1);
    showActionPrompt({
      kind: "manual_install",
      title: standalone ? "Broadway Red è già installata" : "Installa Broadway Red",
      body: standalone
        ? "Stai già usando Broadway Red come app."
        : (ios
          ? "In Safari tocca Condividi e poi Aggiungi alla schermata Home."
          : "Apri il menu del browser e scegli Installa app o Aggiungi alla schermata Home."),
      actionLabel: "Ho capito",
      action: async () => true
    });
  };

  const requestInstall = async () => {
    const result = await window.BRPWAInstall();
    if (result.status === "accepted") return;
    showManualInstall();
  };

  document.addEventListener("click", event => {
    const link = event.target.closest?.("a[href]");
    if (!link) return;
    const href = String(link.getAttribute("href") || "");
    if (standalone) {
      try {
        const target = new URL(href, window.location.href);
        if (target.origin === window.location.origin && target.pathname === "/") {
          event.preventDefault();
          window.location.assign(APP_HOME_URL);
          return;
        }
      } catch (_error) {}
    }
    if (href !== LEGACY_INSTALL_URL && href !== INSTALL_HASH && !href.endsWith(INSTALL_HASH)) return;
    event.preventDefault();
    requestInstall().catch(() => showManualInstall());
  });

  const offerPush = async registration => {
    if (!standalone || config.customerAuthenticated !== true) return;
    if (!("Notification" in window) || !("PushManager" in window) || Notification.permission === "denied") return;
    const existing = await registration.pushManager.getSubscription();
    if (existing) {
      let statusResponse;
      try {
        statusResponse = await fetch("/apps/broadway-club/pwa/subscriptions/status", {
          method: "POST", headers: {"Content-Type": "application/json"},
          body: JSON.stringify({endpoint: existing.endpoint}), credentials: "same-origin"
        });
      } catch (_error) { return; }
      if (!statusResponse.ok) return;
      const status = await statusResponse.json();
      if (status.status === "registered") return;
      if (status.status !== "not_registered") return;
      await existing.unsubscribe();
    }
    showActionPrompt({
      kind: "push",
      title: "Notifiche Broadway Red",
      body: "Attiva le notifiche per ricevere aggiornamenti Club, premi e proposte personali.",
      actionLabel: "Attiva notifiche",
      action: async () => (await window.BRPWAEnablePush("Broadway Red App")).status === "registered"
    });
  };

  window.BRPWA.registrationPromise = removeLegacyProgressierRuntime().then(() => navigator.serviceWorker.register(
    `/apps/broadway-club/pwa-sw.js?v=${encodeURIComponent(WORKER_VERSION)}`,
    {scope: "/apps/broadway-club/", updateViaCache: "none"}
  )).then(async registration => {
    await registration.update();
    const candidate = registration.installing || registration.waiting;
    if (candidate && candidate.state !== "activated") {
      await new Promise((resolve, reject) => {
        const timeout = window.setTimeout(resolve, 5000);
        candidate.addEventListener("statechange", () => {
          if (candidate.state === "activated") {
            window.clearTimeout(timeout);
            resolve();
          } else if (candidate.state === "redundant") {
            window.clearTimeout(timeout);
            reject(new Error("PWA worker update became redundant"));
          }
        });
      });
    }
    if (standalone && config.customerAuthenticated === true) {
      const response = await fetch("/apps/broadway-club/pwa/standalone-verification", {
        method: "POST", headers: {"Content-Type": "application/json"},
        body: JSON.stringify({display_mode: "standalone"}), credentials: "same-origin"
      });
      state("standalone_report", {status: response.ok ? "verified" : "rejected"});
    }
    window.BRPWA.registration = registration;
    state("worker_registered", {standalone});
    if (registration.active) registration.active.postMessage({type: "BRPWA_VERSION_REQUEST"});
    await offerPush(registration);
    return registration;
  }).catch(error => {
    state("worker_error");
    throw error;
  });

  window.BRPWAEnablePush = async deviceLabel => {
    if (!("Notification" in window) || !("PushManager" in window)) return {status: "unsupported"};
    if (!String(config.vapidPublicKey || "")) return {status: "unavailable"};
    const permission = await Notification.requestPermission();
    if (permission !== "granted") return {status: permission};
    const registration = await activeRegistration();
    const subscription = await registration.pushManager.subscribe({
      userVisibleOnly: true, applicationServerKey: vapidKey(String(config.vapidPublicKey || ""))
    });
    const payload = subscription.toJSON();
    payload.device_label = String(deviceLabel || "Broadway Red App").slice(0, 120);
    const response = await fetch("/apps/broadway-club/pwa/subscriptions", {
      method: "POST", headers: {"Content-Type": "application/json"},
      body: JSON.stringify(payload), credentials: "same-origin"
    });
    const result = await response.json();
    state("push_registration", {status: response.ok ? "registered" : "rejected"});
    return result;
  };

  window.BRPWADisablePush = async () => {
    const registration = await activeRegistration();
    const subscription = await registration.pushManager.getSubscription();
    if (!subscription) return {status: "not_registered"};
    const response = await fetch("/apps/broadway-club/pwa/subscriptions", {
      method: "DELETE", headers: {"Content-Type": "application/json"},
      body: JSON.stringify({endpoint: subscription.endpoint}), credentials: "same-origin"
    });
    if (response.ok) await subscription.unsubscribe();
    const result = await response.json();
    state("push_revocation", {status: response.ok ? "revoked" : "rejected"});
    return result;
  };
})();
